import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Grid,
  Container,
  Skeleton,
  TextField,
  Paper,
  Typography,
} from "@mui/material";
import * as caseStudyService from "../../../services/CasestudyService";
import Notification from "../../../../../components/mui/Notification";
import debounce from "lodash/debounce";
import { isEpmty } from "../../../../../helper/TextHelper";

export const TitleIntro = ({ caseStudyId, handleUpdatingStatus }) => {
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({
    title: "",
    introduction: "",
  });
  const [errors, setErrors] = useState({});

  const updateTitle = useCallback(
    (value) => {
      handleUpdatingStatus(true);
      const request = {
        caseStudyId,
        title: value,
      };
      caseStudyService
        .updateTitle(request)
        .then((res) => {})
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message: "Could not update title. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus, caseStudyId]
  );

  const updateIntroduction = useCallback(
    (value) => {
      handleUpdatingStatus(true);
      const request = {
        caseStudyId,
        text: value,
      };
      caseStudyService
        .updateIntro(request)
        .then((res) => {})
        .catch((e) => {
          console.error(e);
          setNotify({
            isOpen: true,
            message: "Could not update introduction. Please try again later",
            type: "error",
          });
        })
        .finally(() => {
          handleUpdatingStatus(false);
        });
    },
    [handleUpdatingStatus, caseStudyId]
  );

  // Debounced save function (memoized to keep reference stable)
  const debouncedSave = useMemo(
    () =>
      debounce((name, value) => {
        if ("title" === name) {
          updateTitle(value);
        } else if ("introduction" === name) {
          updateIntroduction(value);
        }
      }, 2000),
    [updateTitle, updateIntroduction]
  );

  const handleChanges = (e) => {
    const { name, value } = e.target;

    setValues({
      ...values,
      [name]: value,
    });

    if (isEpmty(value)) {
      setErrors((prevValues) => ({
        ...prevValues,
        [name]: "This field can not be empty.",
      }));
    } else {
      setErrors((prevValues) => ({
        ...prevValues,
        [name]: undefined,
      }));
      debouncedSave(name, value);
    }
  };

  useEffect(() => {
    caseStudyService
      .titleAndIntroduction(caseStudyId)
      .then((res) => {
        setValues({
          ...values,
          title: res.data.title,
          introduction: res.data.introduction,
        });
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message:
            "Could not fetch title and introduction. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <Paper variant="outlined" sx={{ my: { xs: 1 }, p: { xs: 2 } }}>
        <Grid container spacing={3} sx={{ p: { xs: 1, md: 1 } }}>
          <Grid item>
            <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
              Title
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <Skeleton variant="text" sx={{ fontSize: "4rem" }} />
            ) : (
              <TextField
                name="title"
                fullWidth
                value={values.title}
                onChange={handleChanges}
                error={!!errors.title}
                helperText={errors.title}
              />
            )}
          </Grid>

          <Grid item>
            <Typography sx={{ fontWeight: "bold" }} variant="h6" gutterBottom>
              Introduction
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <Skeleton variant="rounded" sx={{ fontSize: "10rem" }} />
            ) : (
              <TextField
                name="introduction"
                multiline
                fullWidth
                value={values.introduction}
                onChange={handleChanges}
                error={!!errors.introduction}
                helperText={errors.introduction}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Container>
  );
};
